import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('announcement:slides-updated', this.#syncAfterSlidesChanged);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.current = 0;
    this.#syncAfterSlidesChanged();
    this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  syncSlides() {
    this.#syncAfterSlidesChanged();
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || this.#visibleSlides.length <= 1) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.#slides;
    if (slides.length === 0) return;

    const direction = current < this.#current ? -1 : 1;
    let relativeIndex = ((current % slides.length) + slides.length) % slides.length;

    for (let attempt = 0; attempt < slides.length; attempt++) {
      if (!slides[relativeIndex].hasAttribute('hidden')) break;
      relativeIndex = (relativeIndex + direction + slides.length) % slides.length;
    }

    this.#current = relativeIndex;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  get #slides() {
    if (Array.isArray(this.refs.slides) && this.refs.slides.length > 0) {
      return this.refs.slides;
    }

    return Array.from(this.querySelectorAll('.announcement-bar__slides slideshow-slide'));
  }

  get #visibleSlides() {
    return this.#slides.filter((slide) => slide.hidden === false && !slide.hasAttribute('hidden'));
  }

  #setArrowVisibility(visibleCount) {
    const hideArrows = visibleCount <= 1;
    this.toggleAttribute('data-single-slide', hideArrows);

    const arrows = this.querySelector('slideshow-arrows');
    if (arrows instanceof HTMLElement) {
      arrows.hidden = hideArrows;
    }

    const previous = this.refs.previous;
    const next = this.refs.next;
    if (previous instanceof HTMLElement) {
      previous.hidden = hideArrows;
    }
    if (next instanceof HTMLElement) {
      next.hidden = hideArrows;
    }
  }

  #setBarVisibility(visibleCount) {
    const hideBar = visibleCount === 0;
    const aside = this.closest('.announcement-bar');
    const section = this.closest('.shopify-section');

    if (aside instanceof HTMLElement) {
      aside.hidden = hideBar;
    }

    if (section instanceof HTMLElement) {
      section.hidden = hideBar;
    }
  }

  #syncAfterSlidesChanged = () => {
    const visibleCount = this.#visibleSlides.length;
    this.#setArrowVisibility(visibleCount);
    this.#setBarVisibility(visibleCount);

    if (visibleCount <= 1) {
      this.pause();
    }

    this.current = this.#current;
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
